import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';

interface SupportForm {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const subjectOptions = [
  { value: 'technical_issue', label: 'Problema Técnico' },
  { value: 'access_problem', label: 'Problema de Acesso' },
  { value: 'feature_request', label: 'Solicitação de Funcionalidade' },
  { value: 'bug_report', label: 'Relatório de Bug' },
  { value: 'integration_help', label: 'Ajuda com Integração' },
  { value: 'performance_issue', label: 'Problema de Performance' },
  { value: 'security_concern', label: 'Preocupação de Segurança' },
  { value: 'training_request', label: 'Solicitação de Treinamento' },
  { value: 'billing_question', label: 'Dúvida sobre Cobrança' },
  { value: 'other', label: 'Outro' }
];

export const SupportPage: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<SupportForm>({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: ''
  });

  const handleFormChange = (field: keyof SupportForm, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.subject || !form.message) {
      addToast('Por favor, preencha todos os campos obrigatórios', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simular envio do e-mail (em produção, isso seria uma chamada para a API)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Aqui você implementaria a lógica real de envio do e-mail

      addToast('Ticket de suporte criado com sucesso! Entraremos em contato em breve.', 'success');
      
      // Limpar formulário
      setForm(prev => ({
        ...prev,
        subject: '',
        message: ''
      }));
    } catch (error) {
      console.error('Erro ao enviar formulário de suporte:', error);
      addToast('Erro ao enviar solicitação. Tente novamente.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Suporte Técnico</h1>
          <p className="text-gray-600">
            Estamos aqui para ajudar! Preencha o formulário abaixo e nossa equipe entrará em contato.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => handleFormChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled
              />
              <p className="text-xs text-gray-500 mt-1">
                Preenchido automaticamente com seu nome de usuário
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                E-mail *
              </label>
              <input
                type="email"
                value={form.email}
                onChange={e => handleFormChange('email', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                Preenchido automaticamente com seu e-mail
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assunto *
            </label>
            <select
              value={form.subject}
              onChange={e => handleFormChange('subject', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Selecione um assunto</option>
              {subjectOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem *
            </label>
            <textarea
              value={form.message}
              onChange={e => handleFormChange('message', e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descreva detalhadamente sua solicitação, problema ou dúvida. Quanto mais informações você fornecer, melhor poderemos ajudá-lo."
              required
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Dicas para um atendimento mais eficiente
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Inclua detalhes específicos sobre o problema</li>
                    <li>Mencione passos para reproduzir o erro (se aplicável)</li>
                    <li>Informe qual navegador e sistema operacional está usando</li>
                    <li>Adicione capturas de tela se necessário</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Funcionalidade Bloqueada
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    O envio de solicitações de suporte foi bloqueado pelo administrador do sistema. 
                    Para suporte urgente, entre em contato diretamente através do e-mail: 
                    <a href="mailto:suporte@baruk.ai" className="text-yellow-800 font-medium ml-1 hover:underline">
                      suporte@baruk.ai
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={true}
              className="w-full md:w-auto"
            >
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Funcionalidade Bloqueada
              </>
            </Button>
          </div>
        </form>

        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Outras formas de contato</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">E-mail Direto</h4>
              <p className="text-sm text-gray-600 mb-2">
                Para questões urgentes, você pode nos contatar diretamente:
              </p>
              <a 
                href="mailto:suporte@baruk.ai" 
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                suporte@baruk.ai
              </a>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Horário de Atendimento</h4>
              <p className="text-sm text-gray-600">
                Segunda a Sexta: 8h às 18h<br/>
                Sábado: 9h às 13h<br/>
                <span className="text-xs text-gray-500">
                  (Horário de Brasília)
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 